import type * as Ably from 'ably';
import type { Value } from 'ably/liveobjects';

import type { ChannelRootSnapshot } from '@lib/live/match/channels';

/**
 * Objects mode makes Ably deliver the full state of a channel's objects on
 * attach and then stream subsequent operations. It is what replaces the old
 * snapshot-then-replay bootstrap: there is no window in which the client holds
 * partial state, so there is nothing to version-gap detect or buffer.
 */
const OBJECTS_CHANNEL_MODE = 'OBJECT_SUBSCRIBE' as const;

/**
 * Channel states that mean this channel has stopped delivering.
 *
 * `suspended` is usually transient and Ably resyncs object state on reattach,
 * but neither state resolves itself while the page sits there: a `failed`
 * channel never comes back on its own. Without this the pitch would keep
 * rendering the last values it happened to receive, still badged live.
 */
const UNAVAILABLE_CHANNEL_STATES = ['failed', 'suspended'] as const;

export type ChannelSubscription = {
  close: () => void;
};

type SubscribeOptions = {
  onSnapshot: (snapshot: ChannelRootSnapshot) => void;
  /** Called when the channel stops delivering, with the state that caused it. */
  onUnavailable?: (state: string) => void;
};

/**
 * Attach a channel in objects mode and hand its root LiveMap to `onSnapshot`
 * as a plain object, once with the synced state and again on every change.
 *
 * The returned teardown both unsubscribes and detaches. Detaching matters:
 * a squad can span ten fixtures and forty player channels, and an attached
 * channel keeps costing messages after the tile that wanted it has gone.
 */
export const subscribeToChannelRoot = async (
  client: Ably.Realtime,
  channelName: string,
  { onSnapshot, onUnavailable }: SubscribeOptions
): Promise<ChannelSubscription> => {
  const channel = client.channels.get(channelName, {
    modes: [OBJECTS_CHANNEL_MODE]
  });

  const onStateChange = (stateChange: Ably.ChannelStateChange) => {
    onUnavailable?.(stateChange.current);
  };
  for (const state of UNAVAILABLE_CHANNEL_STATES) {
    channel.on(state, onStateChange);
  }

  let root;
  try {
    root = await channel.object.get<Record<string, Value>>();
  } catch (error) {
    for (const state of UNAVAILABLE_CHANNEL_STATES) {
      channel.off(state, onStateChange);
    }
    // Getting the root can fail after the attach succeeded, which would leave
    // the channel attached with nothing holding a handle to detach it.
    void channel.detach().catch(() => undefined);
    throw error;
  }

  /**
   * An empty root is not news, so it is not delivered.
   *
   * Attaching emits immediately, and a channel the engine has not published to
   * yet carries nothing. Passing that on would hand the projections an empty
   * object, which they would faithfully turn into a SCHEDULED match with no
   * kickoff and a player on zero points, overwriting whatever the REST snapshot
   * had already established.
   */
  const emit = () => {
    const snapshot = root.compact();
    if (snapshot === undefined) return;
    if (Object.keys(snapshot).length === 0) return;
    onSnapshot(snapshot as ChannelRootSnapshot);
  };

  emit();
  const subscription = root.subscribe(() => emit());

  return {
    close: () => {
      subscription.unsubscribe();
      for (const state of UNAVAILABLE_CHANNEL_STATES) {
        channel.off(state, onStateChange);
      }
      // Detach can reject, most likely when teardown follows a connection that
      // was already failing. Nothing downstream can act on it, but an
      // unhandled rejection would surface as a page-level error.
      void channel.detach().catch((error: unknown) => {
        console.warn('[live] channel detach failed', { channelName, error });
      });
    }
  };
};
